import styled from '@emotion/styled';
import { formatDateShort, useResource } from '@landsbankinn/l16n';
import type { Prismic } from '@landsbankinn/prismic';
import { device, Divider, typography } from '@landsbankinn/ui';
import Link from 'next/link';
import React, { forwardRef } from 'react';
import { animated } from 'react-spring';

const Host = styled(animated.div)`
  display: block;
  position: relative;
  width: 100%;
`;

const Date = styled.div`
  ${typography.body}
  opacity: 0.75;
  color: ${(props) => props.theme.colors.foreground};
  margin-bottom: 13px;
`;

const A = styled.a`
  display: block;

  &:hover {
    text-decoration: underline;
  }
  @media ${device.tablet} {
    min-height: 73px;
  }
`;

const Title = styled.h3<{ maxLines?: number }>`
  ${typography.h4}
  color: ${(props) => props.theme.colors.foreground};
  margin-bottom: 0;

  ${(props) =>
    props.maxLines &&
    `
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: ${props.maxLines};
    -webkit-box-orient: vertical;
    position: relative;
    
    
    &:after {
      display: block;
      content: '';
      position: absolute;
      width: 50%;
      height: 27px;
      background-image: linear-gradient(90deg, rgba(244,247,247,0) 0%, ${props.theme.colors.backgroundAlt} 60%);
      bottom: 0;
      right: 0;
    }
  `}
  @media ${device.tablet} {
    ${(props) =>
      props.maxLines &&
      `
    height: 96px;
    `}
  }

  @media ${device.desktop} {
    ${(props) =>
      props.maxLines &&
      `
    margin-bottom: 45px;
    `}
  }
`;

const Copy = styled.div<{ maxLines?: number }>`
  ${typography.body}
  color: ${(props) => props.theme.colors.foreground};
  ${(props) =>
    props.maxLines &&
    `
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: ${props.maxLines};
    -webkit-box-orient: vertical;
    position: relative;
    &:after {
      display: block;
      content: '';
      position: absolute;
      width: 50%;
      height: 27px;
      background-image: linear-gradient(90deg, rgba(244,247,247,0) 0%, ${props.theme.colors.backgroundAlt} 60%);
      bottom: 0;
      right: 0;
    }
  `}
`;

interface NewsCardProps {
  date: Date;
  link: Prismic.NextLink;
  title: string;
  description: string;
  length: number;
  [key: string]: any;
}

export const NewsCard = forwardRef<HTMLDivElement, NewsCardProps>(
  function NCard({ date, title, description, link, ...rest }, ref) {
    const { locale } = useResource();
    const anchorProps = link?.anchorProps?.();

    if (title === anchorProps?.title) {
      anchorProps.title = undefined;
    }
    return (
      <Host ref={ref} {...rest}>
        <Date>{formatDateShort(locale, date)}</Date>
        <Link {...link} prefetch={false}>
          <A {...anchorProps}>
            <Title maxLines={3}>{title}</Title>
          </A>
        </Link>
        <Divider margin="24px 0" style={{ height: 2 }} />
        <Copy maxLines={5}>{description}</Copy>
      </Host>
    );
  },
);
